import { Component, Inject, OnInit, Optional, Provider, SkipSelf } from '@angular/core';
import {
  provideVisualization,
  VISUALIZER,
  IVisualizer,
  IMAGE_STATE_PORT,
  TILE_ACCESS_PORT,
  REGION_IO_PORT,
  VIZ_CONFIG,
  SPATIAL_DATA_PORT,
  TOOLBAR_TOOLS,
  ToolbarContribution,
  ToolbarToolVisibility,
  isDialogToolContribution,
} from '@jax-data-science/sci-image-visualizer';
import { ProcessingImage } from '../../../models/processing-pipeline';
import { PipelineImageStateAdapter } from './pipeline-image-state.adapter';
import { PipelineTileAccessAdapter, PipelineRegionIoAdapter } from './pipeline-viz-ports';

/**
 * The preview's toolbar tools: the app's, minus the dialog tools.
 *
 * Run tools (YOLO, retinal) work on whatever this viewer shows, so the preview keeps
 * them. A dialog tool does not: its contribution is created in the root injector, so
 * it reads the MAIN viewer's file through the root `TILE_ACCESS_PORT`, while its
 * dialog would draw over this viewer's image. DIANNE there would train on the main
 * image's STQ features at the preview's coordinates. So the preview offers none, and
 * the main viewer (which injects the root list) is unaffected.
 */
export const PIPELINE_PREVIEW_TOOLBAR_TOOLS: Provider = {
  provide: TOOLBAR_TOOLS,
  useFactory: (appTools: readonly ToolbarContribution[] | null): ToolbarContribution[] =>
    (appTools ?? []).filter((t) => !isDialogToolContribution(t)),
  deps: [[new Optional(), new SkipSelf(), TOOLBAR_TOOLS]],
};

/**
 * Embeds the shared `jax-image-visualization` viewer in the processing-pipeline
 * dialog, replacing the bespoke Plotly preview.
 *
 * `provideVisualization()` gives this subtree its OWN backend chain (router /
 * Plotly / OSD / stores / tools), isolated from the main diagram's — so drawing
 * regions or loading a preview here never clobbers the main viewer's state. The
 * host ports are pipeline-specific: an in-memory image source (no tile server)
 * and no GeoJSON persistence. The Image type renders through the OSD viewer
 * (via the simple-image path, since the image carries `tiled: false`).
 */
@Component({
  selector: 'app-pipeline-preview',
  template: '<visualizer [toolbarTools]="toolbarTools"></visualizer>',
  styles: [':host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }'],
  providers: [
    ...provideVisualization(),
    PipelineImageStateAdapter,
    { provide: IMAGE_STATE_PORT, useExisting: PipelineImageStateAdapter },
    { provide: TILE_ACCESS_PORT, useClass: PipelineTileAccessAdapter },
    { provide: REGION_IO_PORT, useClass: PipelineRegionIoAdapter },
    { provide: VIZ_CONFIG, useValue: { slideCropServer: '' } },
    // No spatial-omics data: the root port publishes the MAIN viewer's dataset, which the
    // preview would draw (into a closed dialog it isn't attached to yet, alerting "Could not
    // draw the dataset … the renderer could not start" over the main viewer).
    { provide: SPATIAL_DATA_PORT, useValue: null },
    PIPELINE_PREVIEW_TOOLBAR_TOOLS,
  ],
})
export class PipelinePreviewComponent implements OnInit {
  /** Pipeline preview shows the zoom, region, and special tools (plot-type/
   *  channels/download/autoscale) — no help. */
  readonly toolbarTools: ToolbarToolVisibility = {
    specialTools: true,
    zoomTools: true,
    regionTools: true,
    help: false,
  };

  /** Preview-pixel → original-image affine for region export: an offset (the
   *  crop's origin in the original image) plus a per-axis scale. The viewer works
   *  in the blob's own pixels; the server wants original full-res coordinates.
   *  When the whole image is in view the offset is 0 and this reduces to the old
   *  `dx = ratio` scale; when opened from a zoom the offset places drawn regions
   *  at their true location in the original image (jit-ui#95 scale support). */
  private offsetX = 0;
  private offsetY = 0;
  private ratioX = 1;
  private ratioY = 1;

  constructor(
    private imageState: PipelineImageStateAdapter,
    @Inject(VISUALIZER) private viz: IVisualizer,
  ) {}

  ngOnInit(): void {
    // No overview minimap in the small embedded preview, and render raw pixels
    // (no smoothing) so processed images are inspectable at the pixel level. Set
    // before the first render so the OSD viewer is created with these settings.
    this.viz.setNavigatorVisible(false);
    this.viz.setImageSmoothingEnabled(false);
  }

  /** Show `image` in the viewer. `fullImageSize` is the source image's true size
   *  when the preview is downsampled. `sourceRect` is the region of the original
   *  image these pixels cover (origin + extent in original pixel coords) when the
   *  dialog was opened from a zoomed/cropped view — pass it so drawn regions map
   *  back to their true location in the original image, not just scale from 0,0.
   *  When `sourceRect` is absent we fall back to the full-image scale (preview is
   *  treated as a downsample of the whole image — the prior behaviour). */
  async setImage(
    image: ProcessingImage | null,
    fullImageSize: { width: number; height: number } | null,
    sourceRect: { x: number; y: number; width: number; height: number } | null = null,
    sourcePixelSize: { mppX: number | null; mppY: number | null } | null = null,
  ): Promise<void> {
    if (image && sourceRect) {
      this.offsetX = sourceRect.x;
      this.offsetY = sourceRect.y;
      this.ratioX = sourceRect.width / image.width;
      this.ratioY = sourceRect.height / image.height;
    } else if (image && fullImageSize) {
      this.offsetX = 0;
      this.offsetY = 0;
      this.ratioX = fullImageSize.width / image.width;
      this.ratioY = fullImageSize.height / image.height;
    } else {
      this.offsetX = 0;
      this.offsetY = 0;
      this.ratioX = 1;
      this.ratioY = 1;
    }
    // Each preview pixel spans `ratio` original pixels, so the preview's physical
    // pixel size is the original µm/px × that ratio — this keeps the scale bar
    // physically correct on a zoomed crop. null mpp → no bar (as on the main viewer).
    const pixelSize = sourcePixelSize
      ? {
          mppX: sourcePixelSize.mppX != null ? sourcePixelSize.mppX * this.ratioX : null,
          mppY: sourcePixelSize.mppY != null ? sourcePixelSize.mppY * this.ratioY : null,
        }
      : null;
    await this.imageState.setImage(image, pixelSize);
  }

  /** Regions drawn on the preview, as Polygons in ORIGINAL full-resolution
   *  coordinates (offset + scaled from preview pixels) — the shape the COMPOUND
   *  job expects, so they line up with the original dataset. */
  getRegionPolygons(): any[] {
    const polys = this.viz.getRegionPolygons() ?? [];
    if (this.offsetX === 0 && this.offsetY === 0 && this.ratioX === 1 && this.ratioY === 1) {
      return polys;
    }
    return polys.map((p: any) => ({
      ...p,
      xpoints: (p.xpoints ?? []).map((x: number) => Math.round(this.offsetX + x * this.ratioX)),
      ypoints: (p.ypoints ?? []).map((y: number) => Math.round(this.offsetY + y * this.ratioY)),
    }));
  }

  /** Show regions on the preview, in this preview image's own pixel coordinates.
   *  Used to display an engine's detected objects (YOLO) where the user is
   *  actually looking, rather than only on the main viewer behind the dialog. */
  setRegions(regions: any[]): void {
    this.viz.setRegions(regions);
  }

  /** The preview's current regions — including any the user drew, which the
   *  COMPOUND server submission reads back through {@link getRegionPolygons}. */
  getRegions(): any[] {
    return this.viz.getRegions?.() ?? [];
  }

  hasRegions(): boolean {
    return (this.viz.getRegions?.() ?? []).length > 0;
  }

  clearRegions(): void {
    this.viz.setRegions([]);
  }
}
